// Define a transporter (ACPL, VRL, …) or add a new one, from the Dispatch
// hand-off. The details are what Accounts needs for the e-way bill and what
// prints on the challan and invoice. Saved through /api/transporters, which
// BDMs may use without general master-data rights.

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { X } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';

export interface TransporterDetails {
  id?: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  contactPerson: string;
  contactPersonPhone: string;
  gstin: string;
}

const EMPTY: TransporterDetails = { name: '', address: '', phone: '', email: '', contactPerson: '', contactPersonPhone: '', gstin: '' };

export function useTransporters() {
  return useQuery({
    queryKey: ['transporters'],
    queryFn: async () => (await api.get<{ transporters: TransporterDetails[] }>('/api/transporters')).data.transporters,
    staleTime: 60_000,
  });
}

/**
 * `name` set  → Define (view / edit that transporter; a name from the fixed
 *               list gets its entry the first time it is saved).
 * `name` ''   → New transporter.
 */
export function TransporterDefineModal({ name, onClose, onSaved }: {
  name: string;
  onClose: () => void;
  onSaved: (saved: TransporterDetails) => void;
}) {
  const qc = useQueryClient();
  const { data: list = [] } = useTransporters();
  const [form, setForm] = useState<TransporterDetails>({ ...EMPTY, name });

  useEffect(() => {
    const found = list.find((t) => t.name.toLowerCase() === name.toLowerCase());
    setForm(found ? { ...EMPTY, ...found } : { ...EMPTY, name });
  }, [name, list]);

  const save = useMutation({
    mutationFn: async () => (await api.put<{ transporter: TransporterDetails }>('/api/transporters', { ...form, originalName: name || form.name })).data.transporter,
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['transporters'] });
      qc.invalidateQueries({ queryKey: ['masters', 'transportation'] });
      toast.success(`${saved.name} saved.`);
      onSaved(saved);
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not save the transporter.')),
  });

  const set = (k: keyof TransporterDetails) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const field = 'w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20';
  const label = (text: string, required = false) => (
    <span className="mb-1 block text-xs font-semibold text-slate-600">{text}{required && <span className="text-rose-500"> *</span>}</span>
  );

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/60 p-4" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 pb-3">
          <h3 className="text-sm font-bold text-slate-900">{name ? `Transportation details (${name})` : 'New transporter'}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 overflow-y-auto px-5 pb-2">
          <p className="text-[11px] text-slate-500">Accounts uses these for the e-way bill; they also print on the challan and invoice.</p>
          <label className="block">{label('Name', true)}<input className={field} value={form.name} onChange={set('name')} /></label>
          <label className="block">{label('Address', true)}<textarea rows={3} className={field} value={form.address} onChange={set('address')} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">{label('Phone', true)}<input className={field} value={form.phone} onChange={set('phone')} inputMode="tel" /></label>
            <label className="block">{label('Email')}<input className={field} value={form.email} onChange={set('email')} type="email" /></label>
            <label className="block">{label('Contact person')}<input className={field} value={form.contactPerson} onChange={set('contactPerson')} /></label>
            <label className="block">{label('Contact person phone')}<input className={field} value={form.contactPersonPhone} onChange={set('contactPersonPhone')} inputMode="tel" /></label>
          </div>
          <label className="block">{label('GSTIN (transporter ID for the e-way bill)')}<input className={`${field} uppercase`} value={form.gstin} onChange={set('gstin')} maxLength={15} /></label>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold">Cancel</button>
          <button type="button" onClick={() => save.mutate()}
            disabled={save.isPending || !form.name.trim() || !form.address.trim() || !form.phone.trim()}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
